// Pure drag-chase physics — velocity steering with capped speed/acceleration.

export const FIREFLY_DRAG_CHASE_SAMPLE_MS = 250;

/** Max travel per animation frame (~330px/s at 60fps). */
export const FIREFLY_CHASE_MAX_SPEED_PX = 5.5;
/** Max velocity-vector change per frame. */
export const FIREFLY_CHASE_MAX_ACCEL_PX = 0.44;
/** How quickly heading blends toward the target (lower = wider curves). */
export const FIREFLY_CHASE_HEADING_BLEND = 0.07;
/** How quickly the effective chase point eases toward each sampled target. */
export const FIREFLY_CHASE_TARGET_BLEND = 0.14;
/** Settle-to-anchor cruises faster than pursuit (2× pursuit cap). */
export const FIREFLY_CHASE_SETTLE_SPEED_MULT = 2;
/** Ease settle target toward DOM anchor (avoids release target snap). */
export const FIREFLY_CHASE_SETTLE_TARGET_BLEND = 0.2;

const ARRIVE_THRESHOLD_PX = 3;
const ARRIVAL_SLOW_RADIUS_PX = 48;

export type FireflyChaseMotion = {
  x: number;
  y: number;
  vx: number;
  vy: number;
};

export type FireflyChasePhase = "pursuit" | "settle";

export function fireflyAnchorScreenFromTabRect(
  rect: DOMRect,
  localAnchorX: number,
  localAnchorY: number,
): { x: number; y: number } {
  return {
    x: rect.left + localAnchorX,
    y: rect.top + localAnchorY,
  };
}

/** @deprecated Use fireflyAnchorScreenFromTabRect with a live tab rect. */
export function anchorScreenFromDragCenter(
  dragCenterX: number,
  dragCenterY: number,
  tabWidthPx: number,
  tabHeightPx: number,
  localAnchorX: number,
  localAnchorY: number,
): { x: number; y: number } {
  return {
    x: dragCenterX + (localAnchorX - tabWidthPx / 2),
    y: dragCenterY + (localAnchorY - tabHeightPx / 2),
  };
}

export function lerpAngleRad(
  current: number,
  target: number,
  blend: number,
): number {
  let delta = target - current;
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  return current + delta * blend;
}

export function blendChaseTarget(
  current: { x: number; y: number },
  sampled: { x: number; y: number },
  blend = FIREFLY_CHASE_TARGET_BLEND,
): { x: number; y: number } {
  return {
    x: current.x + (sampled.x - current.x) * blend,
    y: current.y + (sampled.y - current.y) * blend,
  };
}

export function stepFireflyChaseMotion(
  state: FireflyChaseMotion,
  target: { x: number; y: number },
  phase: FireflyChasePhase = "pursuit",
): FireflyChaseMotion & { arrived: boolean } {
  const speedCap =
    FIREFLY_CHASE_MAX_SPEED_PX *
    (phase === "settle" ? FIREFLY_CHASE_SETTLE_SPEED_MULT : 1);
  const accelCap =
    FIREFLY_CHASE_MAX_ACCEL_PX *
    (phase === "settle" ? FIREFLY_CHASE_SETTLE_SPEED_MULT : 1);

  const dx = target.x - state.x;
  const dy = target.y - state.y;
  const dist = Math.hypot(dx, dy);

  if (dist <= ARRIVE_THRESHOLD_PX) {
    return { x: target.x, y: target.y, vx: 0, vy: 0, arrived: true };
  }

  const desiredHeading = Math.atan2(dy, dx);
  const speed = Math.hypot(state.vx, state.vy);
  const currentHeading =
    speed > 0.08 ? Math.atan2(state.vy, state.vx) : desiredHeading;
  const heading = lerpAngleRad(
    currentHeading,
    desiredHeading,
    FIREFLY_CHASE_HEADING_BLEND,
  );

  const arrivalFactor = Math.min(1, dist / ARRIVAL_SLOW_RADIUS_PX);
  const desiredSpeed = Math.min(
    speedCap,
    Math.max(0.35, arrivalFactor * speedCap),
  );

  const desiredVx = Math.cos(heading) * desiredSpeed;
  const desiredVy = Math.sin(heading) * desiredSpeed;

  let dvx = desiredVx - state.vx;
  let dvy = desiredVy - state.vy;
  const deltaMag = Math.hypot(dvx, dvy);
  if (deltaMag > accelCap) {
    const scale = accelCap / deltaMag;
    dvx *= scale;
    dvy *= scale;
  }

  let vx = state.vx + dvx;
  let vy = state.vy + dvy;
  const newSpeed = Math.hypot(vx, vy);
  if (newSpeed > speedCap) {
    const scale = speedCap / newSpeed;
    vx *= scale;
    vy *= scale;
  }

  const x = state.x + vx;
  const y = state.y + vy;

  const remaining = Math.hypot(target.x - x, target.y - y);
  if (remaining <= ARRIVE_THRESHOLD_PX) {
    return { x: target.x, y: target.y, vx: 0, vy: 0, arrived: true };
  }

  return { x, y, vx, vy, arrived: false };
}
